import { UserServices, getCachedMyProfile } from '../Services/UserServices';

const profileService = new UserServices();

export const LEARNING_HUB_AVAILABILITY_KEY = 'learningHubAvailable';
export const LEARNING_HUB_AVAILABILITY_EVENT = 'learningHubAvailabilityChanged';
export const LEARNING_HUB_ACCESS_KEY = 'learningHubAccess';
export const LEARNING_HUB_ACCESS_EVENT = 'learningHubAccessChanged';
export const LEARNING_HUB_UNAVAILABLE_MESSAGE =
  'Learning Hub is not available for your project';

const LEARNING_HUB_MENU_LABELS = {
  scheduled: 'Not started',
  expired: 'Expired',
};

export const learningHubMenuLabel = (accessOrState) => {
  const state = typeof accessOrState === 'string'
    ? accessOrState
    : accessOrState?.access_state;
  return LEARNING_HUB_MENU_LABELS[state] || '';
};

export const isLearningHubAvailable = () =>
  localStorage.getItem(LEARNING_HUB_AVAILABILITY_KEY) === 'true';

export const isLearningHubUnavailableResponse = (response) =>
  response?.status === false
  && response?.message === LEARNING_HUB_UNAVAILABLE_MESSAGE;

export const rememberLearningHubAvailability = () => ({
  available: localStorage.getItem(LEARNING_HUB_AVAILABILITY_KEY),
  access: localStorage.getItem(LEARNING_HUB_ACCESS_KEY),
});

export const restoreLearningHubAvailability = (snapshot) => {
  if (!snapshot) {
    return;
  }

  if (snapshot.available == null) {
    localStorage.removeItem(LEARNING_HUB_AVAILABILITY_KEY);
  } else {
    localStorage.setItem(LEARNING_HUB_AVAILABILITY_KEY, snapshot.available);
  }

  if (snapshot.access == null) {
    localStorage.removeItem(LEARNING_HUB_ACCESS_KEY);
  } else {
    localStorage.setItem(LEARNING_HUB_ACCESS_KEY, snapshot.access);
  }

  window.dispatchEvent(
    new CustomEvent(LEARNING_HUB_AVAILABILITY_EVENT, {
      detail: { available: snapshot.available === 'true' },
    })
  );
  window.dispatchEvent(
    new CustomEvent(LEARNING_HUB_ACCESS_EVENT, {
      detail: { access: getLearningHubAccess() },
    })
  );
};

export const setLearningHubAvailability = (available) => {
  localStorage.setItem(LEARNING_HUB_AVAILABILITY_KEY, available ? 'true' : 'false');
  window.dispatchEvent(
    new CustomEvent(LEARNING_HUB_AVAILABILITY_EVENT, { detail: { available } })
  );
};

export const getLearningHubAccess = () => {
  try {
    const raw = localStorage.getItem(LEARNING_HUB_ACCESS_KEY);
    if (!raw) {
      return null;
    }

    const access = JSON.parse(raw);
    return access && typeof access === 'object' ? access : null;
  } catch (error) {
    return null;
  }
};

export const setLearningHubAccess = (access) => {
  const nextAccess = access && typeof access === 'object' ? access : null;

  if (nextAccess) {
    localStorage.setItem(LEARNING_HUB_ACCESS_KEY, JSON.stringify(nextAccess));
  } else {
    localStorage.removeItem(LEARNING_HUB_ACCESS_KEY);
  }

  window.dispatchEvent(
    new CustomEvent(LEARNING_HUB_ACCESS_EVENT, { detail: { access: nextAccess } })
  );
};

export const isLearningHubAccessClosed = (access) =>
  ['scheduled', 'expired'].includes(access?.access_state);

export const resolveLearningHubAvailability = (response) => {
  const open = !!(response?.status && (Array.isArray(response.data) || Array.isArray(response.courses)));
  const available = open || isLearningHubAccessClosed(response?.access);
  setLearningHubAvailability(available);
  setLearningHubAccess(available ? response?.access || null : null);
  return available;
};

const normalizeAccessState = (state) => {
  if (state === 'active' || state === 'scheduled' || state === 'expired') {
    return state;
  }
  return null;
};

export const applyLearningHubFromProfile = (profile) => {
  if (!profile || typeof profile.learning_hub_available !== 'boolean') {
    return isLearningHubAvailable();
  }

  const available = profile.learning_hub_available === true;
  const state = available ? normalizeAccessState(profile.learning_hub_access_state) : null;
  setLearningHubAvailability(available);

  if (!available) {
    setLearningHubAccess(null);
    return false;
  }

  const existing = getLearningHubAccess();
  setLearningHubAccess({
    ...(existing && typeof existing === 'object' ? existing : {}),
    access_state: state,
  });
  return true;
};

export const fetchLearningHubAvailability = async () => {
  const profile = await getCachedMyProfile(profileService);
  return applyLearningHubFromProfile(profile);
};

const HUB_SNAPSHOT_KEY = 'deviare.impersonation.hub';
let savedAvailabilitySnapshot = null;
let impersonationHubAvailable = null;
let impersonationHubRequest = null;
let impersonationHubGeneration = 0;

function readSavedHubSnapshot() {
  if (savedAvailabilitySnapshot) {
    return savedAvailabilitySnapshot;
  }
  try {
    const raw = sessionStorage.getItem(HUB_SNAPSHOT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        savedAvailabilitySnapshot = parsed;
        return savedAvailabilitySnapshot;
      }
    }
  } catch (error) {
    sessionStorage.removeItem(HUB_SNAPSHOT_KEY);
  }
  savedAvailabilitySnapshot = rememberLearningHubAvailability();
  try {
    sessionStorage.setItem(HUB_SNAPSHOT_KEY, JSON.stringify(savedAvailabilitySnapshot));
  } catch (error) {
    sessionStorage.removeItem(HUB_SNAPSHOT_KEY);
  }
  return savedAvailabilitySnapshot;
}

export const getImpersonationLearningHubAvailable = () => impersonationHubAvailable;

export const syncImpersonationLearningHub = () => {
  readSavedHubSnapshot();

  if (impersonationHubAvailable !== null) {
    return Promise.resolve(impersonationHubAvailable);
  }

  if (!impersonationHubRequest) {
    const generation = impersonationHubGeneration;
    const snapshotAtStart = savedAvailabilitySnapshot;
    impersonationHubRequest = fetchLearningHubAvailability()
      .then((available) => {
        if (generation !== impersonationHubGeneration) {
          if (!savedAvailabilitySnapshot && snapshotAtStart) {
            restoreLearningHubAvailability(snapshotAtStart);
          }
          return impersonationHubAvailable === true;
        }
        impersonationHubAvailable = available;
        return available;
      });
  }

  return impersonationHubRequest;
};

export const clearImpersonationLearningHub = () => {
  if (
    !savedAvailabilitySnapshot
    && impersonationHubRequest == null
    && impersonationHubAvailable == null
  ) {
    return;
  }

  impersonationHubGeneration += 1;
  const snapshot = savedAvailabilitySnapshot;
  savedAvailabilitySnapshot = null;
  impersonationHubAvailable = null;
  impersonationHubRequest = null;
  sessionStorage.removeItem(HUB_SNAPSHOT_KEY);
  if (snapshot) {
    restoreLearningHubAvailability(snapshot);
  }
};

export const resetImpersonationLearningHubForTests = () => {
  impersonationHubGeneration += 1;
  savedAvailabilitySnapshot = null;
  impersonationHubAvailable = null;
  impersonationHubRequest = null;
  sessionStorage.removeItem(HUB_SNAPSHOT_KEY);
};
